import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccessRequestDialog({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [casaName, setCasaName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setCasaName(""); setMessage(""); setSent(false);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!name.trim()) return toast.error("Inserisci il tuo nome");
    if (!email.trim() || !email.includes("@")) return toast.error("Email non valida");
    if (!casaName.trim()) return toast.error("Inserisci il nome della Casa");

    setSubmitting(true);
    try {
      await axios.post(`${API}/access-requests`, {
        name: name.trim(),
        email: email.trim(),
        casa_name: casaName.trim(),
        message: message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        data-testid="access-request-dialog"
        className="max-w-md rounded-3xl border-[#E8E1D9] bg-white"
      >
        {sent ? (
          <>
            <DialogHeader>
              <div className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <DialogTitle className="text-center font-display text-2xl font-bold text-[#2D2926]">
                Richiesta inviata
              </DialogTitle>
              <DialogDescription className="text-center text-[#5C5855]">
                Riceverai un codice di accesso non appena l'admin della Casa avrà approvato la tua richiesta.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={close}
                className="w-full rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
              >
                Ho capito
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
                <Mail className="h-3 w-3 text-[#FF7C43]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
                  Nuovo membro
                </span>
              </div>
              <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
                Chiedi accesso alla Casa
              </DialogTitle>
              <DialogDescription className="text-[#5C5855]">
                Compila il modulo: l'admin della Casa riceverà la richiesta e ti manderà un codice invito personale.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  Il tuo nome *
                </label>
                <Input
                  data-testid="access-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Es. Zia Maria"
                  className="rounded-xl border-[#E8E1D9] bg-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  Email *
                </label>
                <Input
                  data-testid="access-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="zia.maria@email.com"
                  className="rounded-xl border-[#E8E1D9] bg-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  Casa che vuoi raggiungere *
                </label>
                <Input
                  data-testid="access-casa-input"
                  value={casaName}
                  onChange={(e) => setCasaName(e.target.value)}
                  placeholder="Es. Casa Rossi"
                  className="rounded-xl border-[#E8E1D9] bg-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  Messaggio (opzionale)
                </label>
                <Textarea
                  data-testid="access-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Es. Sono la nipote di Riccardo..."
                  rows={3}
                  className="rounded-xl border-[#E8E1D9] bg-white resize-none"
                />
              </div>
            </div>

            <DialogFooter className="flex flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={close}
                disabled={submitting}
                className="rounded-full border-[#E8E1D9]"
              >
                Annulla
              </Button>
              <Button
                data-testid="access-submit-button"
                onClick={submit}
                disabled={submitting}
                className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
              >
                {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Invia richiesta
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
